/**
 * Main Website JavaScript
 * Dr. Zyc Clinics - Invisalign Specialists
 * Enhanced with accessibility and modern web features
 */ class DrZycWebsite {
    constructor(){
        this.isMenuOpen = false;
        this.init();
    }
    init() {
        this.setupNavigation();
        this.setupSmoothScrolling();
        this.setupFormHandling();
        this.setupAnimations();
        this.setupLazyLoading();
        this.setupVideoPlayButtons();
        this.setupGallery();
        this.setupIntersectionObserver();
    }
    setupNavigation() {
        const navToggle = document.getElementById('nav-toggle');
        const navMenu = document.getElementById('nav-menu');
        const navLinks = document.querySelectorAll('.nav-link');
        // Mobile menu toggle
        navToggle?.addEventListener('click', ()=>{
            this.toggleMobileMenu();
        });
        // Close menu when clicking nav links
        navLinks.forEach((link)=>{
            link.addEventListener('click', ()=>{
                if (this.isMenuOpen) this.closeMobileMenu();
            });
        });
        // Close menu when clicking outside
        document.addEventListener('click', (e)=>{
            if (this.isMenuOpen && !e.target.closest('.nav-container')) this.closeMobileMenu();
        });
        // Handle dropdown menus on mobile
        const dropdowns = document.querySelectorAll('.nav-dropdown');
        dropdowns.forEach((dropdown)=>{
            const link = dropdown.querySelector('.nav-link');
            link?.addEventListener('click', (e)=>{
                if (window.innerWidth <= 768) {
                    e.preventDefault();
                    dropdown.classList.toggle('active');
                }
            });
        });
        // Handle keyboard navigation
        navToggle?.addEventListener('keydown', (e)=>{
            if (e.key === 'Enter' || e.key === ' ') {
                e.preventDefault();
                this.toggleMobileMenu();
            }
        });
    }
    toggleMobileMenu() {
        this.isMenuOpen = !this.isMenuOpen;
        const navMenu = document.getElementById('nav-menu');
        const navToggle = document.getElementById('nav-toggle');
        if (this.isMenuOpen) {
            navMenu?.classList.add('active');
            navToggle?.setAttribute('aria-expanded', 'true');
            document.body.style.overflow = 'hidden';
        } else this.closeMobileMenu();
    }
    closeMobileMenu() {
        this.isMenuOpen = false;
        const navMenu = document.getElementById('nav-menu');
        const navToggle = document.getElementById('nav-toggle');
        navMenu?.classList.remove('active');
        navToggle?.setAttribute('aria-expanded', 'false');
        document.body.style.overflow = '';
    }
    setupSmoothScrolling() {
        // Smooth scrolling for anchor links
        const links = document.querySelectorAll('a[href^="#"]');
        links.forEach((link)=>{
            link.addEventListener('click', (e)=>{
                const href = link.getAttribute('href');
                if (href === '#') return;
                const target = document.querySelector(href);
                if (target) {
                    e.preventDefault();
                    // Calculate offset for fixed header
                    const headerHeight = document.querySelector('.header')?.offsetHeight || 0;
                    const targetPosition = target.offsetTop - headerHeight - 20;
                    window.scrollTo({
                        top: targetPosition,
                        behavior: 'smooth'
                    });
                    // Update focus for accessibility
                    target.setAttribute('tabindex', '-1');
                    target.focus();
                    // Close mobile menu if open
                    if (this.isMenuOpen) this.closeMobileMenu();
                }
            });
        });
    }
    setupFormHandling() {
        const contactForm = document.querySelector('.contact-form');
        if (!contactForm) return;
        contactForm.addEventListener('submit', async (e)=>{
            e.preventDefault();
            await this.handleFormSubmission(contactForm);
        });
        // Add input formatting
        const phoneInput = contactForm.querySelector('input[type="tel"]');
        if (phoneInput) phoneInput.addEventListener('input', (e)=>{
            this.formatPhoneNumber(e.target);
        });
    }
    async handleFormSubmission(form) {
        const submitButton = form.querySelector('.submit-button');
        const originalText = submitButton.textContent;
        try {
            // Show loading state
            submitButton.textContent = "\u05E9\u05D5\u05DC\u05D7...";
            submitButton.disabled = true;
            // Collect form data
            const formData = new FormData(form);
            const data = Object.fromEntries(formData.entries());
            // Simulate form submission (replace with actual endpoint)
            await this.simulateFormSubmission(data);
            // Show success message
            this.showFormMessage("\u05D4\u05D4\u05D5\u05D3\u05E2\u05D4 \u05E0\u05E9\u05DC\u05D7\u05D4 \u05D1\u05D4\u05E6\u05DC\u05D7\u05D4! \u05E0\u05D7\u05D6\u05D5\u05E8 \u05D0\u05DC\u05D9\u05DB\u05DD \u05D1\u05D4\u05E7\u05D3\u05DD.", 'success');
            form.reset();
        } catch (error) {
            console.error('Form submission error:', error);
            this.showFormMessage("\u05E9\u05D2\u05D9\u05D0\u05D4 \u05D1\u05E9\u05DC\u05D9\u05D7\u05EA \u05D4\u05D4\u05D5\u05D3\u05E2\u05D4. \u05D0\u05E0\u05D0 \u05E0\u05E1\u05D5 \u05E9\u05D5\u05D1 \u05DE\u05D0\u05D5\u05D7\u05E8 \u05D9\u05D5\u05EA\u05E8.", 'error');
        } finally{
            // Reset button state
            submitButton.textContent = originalText;
            submitButton.disabled = false;
        }
    }
    async simulateFormSubmission(data) {
        // Simulate API call delay
        return new Promise((resolve, reject)=>{
            setTimeout(()=>{
                // Simulate random success/failure for demo
                if (Math.random() > 0.1) resolve(data);
                else reject(new Error('Simulation error'));
            }, 2000);
        });
    }
    showFormMessage(message, type) {
        // Remove existing message
        const existingMessage = document.querySelector('.form-message');
        if (existingMessage) existingMessage.remove();
        // Create new message
        const messageElement = document.createElement('div');
        messageElement.className = `form-message form-message--${type}`;
        messageElement.setAttribute('role', 'alert');
        messageElement.textContent = message;
        // Style the message
        Object.assign(messageElement.style, {
            padding: '1rem',
            marginTop: '1rem',
            borderRadius: '0.5rem',
            fontSize: '1rem',
            fontWeight: '500',
            backgroundColor: type === 'success' ? '#10b981' : '#ef4444',
            color: '#ffffff',
            textAlign: 'center'
        });
        // Insert message
        const form = document.querySelector('.contact-form');
        form.appendChild(messageElement);
        // Remove message after 5 seconds
        setTimeout(()=>{
            messageElement.remove();
        }, 5000);
    }
    formatPhoneNumber(input) {
        let value = input.value.replace(/\D/g, '');
        // Limit to 10 digits for Israeli phone numbers
        if (value.length > 10) value = value.slice(0, 10);
        // Format as XXX-XXX-XXXX
        if (value.length >= 6) value = value.replace(/(\d{3})(\d{3})(\d{4})/, '$1-$2-$3');
        else if (value.length >= 3) value = value.replace(/(\d{3})(\d+)/, '$1-$2');
        input.value = value;
    }
    setupAnimations() {
        // Add entrance animations for elements
        const animatedElements = document.querySelectorAll('.feature-card, .team-member, .before-after-item');
        animatedElements.forEach((element, index)=>{
            element.style.opacity = '0';
            element.style.transform = 'translateY(30px)';
            element.style.transition = 'opacity 0.6s ease, transform 0.6s ease';
            element.style.transitionDelay = `${index * 0.1}s`;
        });
    }
    setupLazyLoading() {
        // Lazy loading for images
        const images = document.querySelectorAll('img[loading="lazy"]');
        if ('IntersectionObserver' in window) {
            const imageObserver = new IntersectionObserver((entries)=>{
                entries.forEach((entry)=>{
                    if (entry.isIntersecting) {
                        const img = entry.target;
                        img.src = img.src; // Trigger load
                        imageObserver.unobserve(img);
                    }
                });
            });
            images.forEach((img)=>imageObserver.observe(img));
        }
    }
    setupVideoPlayButtons() {
        const playButtons = document.querySelectorAll('.play-button');
        playButtons.forEach((button)=>{
            button.addEventListener('click', ()=>{
                this.handleVideoPlay(button);
            });
            // Keyboard support
            button.addEventListener('keydown', (e)=>{
                if (e.key === 'Enter' || e.key === ' ') {
                    e.preventDefault();
                    this.handleVideoPlay(button);
                }
            });
        });
    }
    handleVideoPlay(button) {
        // Get the video ID from the parent testimonial card
        const testimonialCard = button.closest('.testimonial-card');
        const videoId = testimonialCard?.dataset.videoId;
        if (videoId) this.showVideoModal(videoId, testimonialCard);
        else {
            console.warn('No video ID found for testimonial');
            this.showVideoModal(null, testimonialCard);
        }
    }
    showVideoModal(videoId, card) {
        // Create modal overlay
        const modal = document.createElement('div');
        modal.className = 'video-modal';
        modal.setAttribute('role', 'dialog');
        modal.setAttribute('aria-labelledby', 'video-modal-title');
        modal.setAttribute('aria-modal', 'true');
        // Get testimonial info if available
        const testimonialInfo = card?.querySelector('.testimonial-info h4')?.textContent || "\u05DE\u05D8\u05D5\u05E4\u05DC";
        let videoContent = '';
        if (videoId) // Create YouTube embed
        videoContent = `
                <div class="video-wrapper">
                    <iframe 
                        width="560" 
                        height="315" 
                        src="https://www.youtube.com/embed/${videoId}?autoplay=1&rel=0&modestbranding=1"
                        title="\u{5E1}\u{5E8}\u{5D8}\u{5D5}\u{5DF} \u{5D4}\u{5DE}\u{5DC}\u{5E6}\u{5D4} \u{5E9}\u{5DC} ${testimonialInfo}"
                        frameborder="0" 
                        allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
                        allowfullscreen
                        loading="lazy">
                    </iframe>
                </div>`;
        else // Fallback content
        videoContent = `
                <div class="video-placeholder">
                    <p>\u{5E1}\u{5E8}\u{5D8}\u{5D5}\u{5DF} \u{5D4}\u{5DE}\u{5DC}\u{5E6}\u{5D4} \u{5E9}\u{5DC} ${testimonialInfo}</p>
                    <p>\u{5D1}\u{5D2}\u{5E8}\u{5E1}\u{5D4} \u{5DE}\u{5DC}\u{5D0}\u{5D4}, \u{5DB}\u{5D0}\u{5DF} \u{5D9}\u{5D5}\u{5D8}\u{5DE}\u{5E2} \u{5D4}\u{5E1}\u{5E8}\u{5D8}\u{5D5}\u{5DF} \u{5DE}\u{5D9}\u{5D5}\u{5D8}\u{5D9}\u{5D5}\u{5D1}.</p>
                    <a href="https://www.youtube.com/channel/UCFYX-bvxbe3U_T68RxtYdSQ" target="_blank" rel="noopener noreferrer">
                        \u{5D1}\u{5E7}\u{5E8}\u{5D5} \u{5D1}\u{5E2}\u{5E8}\u{5D5}\u{5E5} \u{5D4}\u{5D9}\u{5D5}\u{5D8}\u{5D9}\u{5D5}\u{5D1} \u{5E9}\u{5DC}\u{5E0}\u{5D5}
                    </a>
                </div>`;
        modal.innerHTML = `
            <div class="video-modal-content">
                <div class="video-modal-header">
                    <h3 id="video-modal-title">\u{5D4}\u{5DE}\u{5DC}\u{5E6}\u{5D4} \u{5E9}\u{5DC} ${testimonialInfo}</h3>
                    <button class="video-modal-close" aria-label="\u{5E1}\u{5D2}\u{5D5}\u{5E8}" type="button">\xd7</button>
                </div>
                <div class="video-modal-body">
                    ${videoContent}
                </div>
            </div>
        `;
        // Style the modal
        Object.assign(modal.style, {
            position: 'fixed',
            top: '0',
            left: '0',
            right: '0',
            bottom: '0',
            backgroundColor: 'rgba(0, 0, 0, 0.9)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            zIndex: '1050',
            padding: '2rem'
        });
        const content = modal.querySelector('.video-modal-content');
        Object.assign(content.style, {
            background: '#ffffff',
            borderRadius: '1rem',
            padding: '0',
            maxWidth: '90vw',
            maxHeight: '90vh',
            width: '800px',
            position: 'relative',
            overflow: 'hidden'
        });
        const header = modal.querySelector('.video-modal-header');
        Object.assign(header.style, {
            padding: '1rem 2rem',
            borderBottom: '1px solid #e5e7eb',
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            backgroundColor: '#f9fafb'
        });
        const body = modal.querySelector('.video-modal-body');
        Object.assign(body.style, {
            padding: videoId ? '0' : '2rem'
        });
        // Style video wrapper for responsive iframe
        const videoWrapper = modal.querySelector('.video-wrapper');
        if (videoWrapper) {
            Object.assign(videoWrapper.style, {
                position: 'relative',
                paddingBottom: '56.25%',
                height: '0',
                overflow: 'hidden'
            });
            const iframe = videoWrapper.querySelector('iframe');
            Object.assign(iframe.style, {
                position: 'absolute',
                top: '0',
                left: '0',
                width: '100%',
                height: '100%'
            });
        }
        const closeButton = modal.querySelector('.video-modal-close');
        Object.assign(closeButton.style, {
            background: 'none',
            border: 'none',
            fontSize: '2rem',
            cursor: 'pointer',
            color: '#6b7280',
            width: '40px',
            height: '40px',
            borderRadius: '50%',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            transition: 'background-color 0.3s ease'
        });
        closeButton.addEventListener('mouseenter', ()=>{
            closeButton.style.backgroundColor = '#f3f4f6';
        });
        closeButton.addEventListener('mouseleave', ()=>{
            closeButton.style.backgroundColor = 'transparent';
        });
        // Add event listeners
        const closeModal = ()=>{
            // Stop video if playing
            const iframe = modal.querySelector('iframe');
            if (iframe) iframe.src = iframe.src; // Reset iframe to stop video
            modal.remove();
            document.body.style.overflow = '';
        };
        closeButton.addEventListener('click', closeModal);
        modal.addEventListener('click', (e)=>{
            if (e.target === modal) closeModal();
        });
        document.addEventListener('keydown', function escapeHandler(e) {
            if (e.key === 'Escape') {
                closeModal();
                document.removeEventListener('keydown', escapeHandler);
            }
        });
        // Add to page
        document.body.appendChild(modal);
        document.body.style.overflow = 'hidden';
        // Focus management
        closeButton.focus();
        // Announce to screen readers
        const announcement = `\u{5E0}\u{5E4}\u{5EA}\u{5D7} \u{5E1}\u{5E8}\u{5D8}\u{5D5}\u{5DF} \u{5D4}\u{5DE}\u{5DC}\u{5E6}\u{5D4} \u{5E9}\u{5DC} ${testimonialInfo}. \u{5DC}\u{5D7}\u{5E5} Escape \u{5DC}\u{5E1}\u{5D2}\u{5D9}\u{5E8}\u{5D4}.`;
        this.createLiveRegion(announcement);
    }
    // Utility method for screen reader announcements
    createLiveRegion(message) {
        // Remove existing live region
        const existing = document.getElementById('video-live-region');
        if (existing) existing.remove();
        // Create new live region
        const liveRegion = document.createElement('div');
        liveRegion.id = 'video-live-region';
        liveRegion.setAttribute('aria-live', 'polite');
        liveRegion.setAttribute('aria-atomic', 'true');
        liveRegion.style.position = 'absolute';
        liveRegion.style.left = '-10000px';
        liveRegion.style.width = '1px';
        liveRegion.style.height = '1px';
        liveRegion.style.overflow = 'hidden';
        document.body.appendChild(liveRegion);
        // Delay to ensure screen readers pick up the change
        setTimeout(()=>{
            liveRegion.textContent = message;
        }, 100);
        // Remove after announcement
        setTimeout(()=>{
            if (liveRegion.parentNode) liveRegion.parentNode.removeChild(liveRegion);
        }, 3000);
    }
    setupGallery() {
        this.currentGalleryImages = [];
        this.currentImageIndex = 0;
        // Setup tab functionality
        const galleryTabs = document.querySelectorAll('.gallery-tab');
        const galleryCategories = document.querySelectorAll('.gallery-category');
        galleryTabs.forEach((tab)=>{
            tab.addEventListener('click', ()=>{
                this.switchGalleryCategory(tab, galleryCategories);
            });
        });
        // Setup modal functionality
        const modal = document.getElementById('gallery-modal');
        const modalImage = document.getElementById('gallery-modal-image');
        const closeBtn = modal?.querySelector('.gallery-modal-close');
        const prevBtn = modal?.querySelector('.gallery-modal-prev');
        const nextBtn = modal?.querySelector('.gallery-modal-next');
        // Setup gallery item click handlers
        this.setupGalleryItems();
        // Modal close handlers
        closeBtn?.addEventListener('click', ()=>{
            this.closeGalleryModal();
        });
        modal?.addEventListener('click', (e)=>{
            if (e.target === modal) this.closeGalleryModal();
        });
        // Navigation handlers
        prevBtn?.addEventListener('click', ()=>{
            this.navigateGallery(-1);
        });
        nextBtn?.addEventListener('click', ()=>{
            this.navigateGallery(1);
        });
        // Keyboard navigation
        document.addEventListener('keydown', (e)=>{
            if (modal?.classList.contains('active')) switch(e.key){
                case 'Escape':
                    this.closeGalleryModal();
                    break;
                case 'ArrowLeft':
                    this.navigateGallery(-1);
                    break;
                case 'ArrowRight':
                    this.navigateGallery(1);
                    break;
            }
        });
    }
    switchGalleryCategory(activeTab, categories) {
        const category = activeTab.dataset.category;
        // Update active tab
        document.querySelectorAll('.gallery-tab').forEach((tab)=>{
            tab.classList.remove('active');
        });
        activeTab.classList.add('active');
        // Show corresponding category
        categories.forEach((cat)=>{
            cat.classList.remove('active');
        });
        const targetCategory = document.querySelector(`.gallery-category[data-category="${category}"]`);
        if (targetCategory) {
            targetCategory.classList.add('active');
            this.setupGalleryItems(); // Re-setup items for the new category
        }
    }
    setupGalleryItems() {
        const galleryItems = document.querySelectorAll('.gallery-category.active .gallery-item');
        // Update current images array
        this.currentGalleryImages = Array.from(galleryItems).map((item)=>{
            const img = item.querySelector('img');
            return {
                src: img.src,
                alt: img.alt
            };
        });
        // Add click handlers
        galleryItems.forEach((item, index)=>{
            item.addEventListener('click', ()=>{
                this.openGalleryModal(index);
            });
            // Keyboard support
            item.addEventListener('keydown', (e)=>{
                if (e.key === 'Enter' || e.key === ' ') {
                    e.preventDefault();
                    this.openGalleryModal(index);
                }
            });
            // Make focusable
            item.setAttribute('tabindex', '0');
            item.setAttribute('role', 'button');
        });
    }
    openGalleryModal(imageIndex) {
        const modal = document.getElementById('gallery-modal');
        const modalImage = document.getElementById('gallery-modal-image');
        if (modal && modalImage && this.currentGalleryImages[imageIndex]) {
            this.currentImageIndex = imageIndex;
            const imageData = this.currentGalleryImages[imageIndex];
            modalImage.src = imageData.src;
            modalImage.alt = imageData.alt;
            modal.classList.add('active');
            modal.setAttribute('aria-hidden', 'false');
            // Focus management
            const closeBtn = modal.querySelector('.gallery-modal-close');
            closeBtn?.focus();
            // Prevent body scroll
            document.body.style.overflow = 'hidden';
        }
    }
    closeGalleryModal() {
        const modal = document.getElementById('gallery-modal');
        if (modal) {
            modal.classList.remove('active');
            modal.setAttribute('aria-hidden', 'true');
            // Restore body scroll
            document.body.style.overflow = '';
            // Return focus to the gallery item that opened the modal
            const activeCategory = document.querySelector('.gallery-category.active');
            const galleryItems = activeCategory?.querySelectorAll('.gallery-item');
            const currentItem = galleryItems?.[this.currentImageIndex];
            currentItem?.focus();
        }
    }
    navigateGallery(direction) {
        if (this.currentGalleryImages.length === 0) return;
        this.currentImageIndex += direction;
        // Loop around
        if (this.currentImageIndex < 0) this.currentImageIndex = this.currentGalleryImages.length - 1;
        else if (this.currentImageIndex >= this.currentGalleryImages.length) this.currentImageIndex = 0;
        // Update modal image
        const modalImage = document.getElementById('gallery-modal-image');
        const imageData = this.currentGalleryImages[this.currentImageIndex];
        if (modalImage && imageData) {
            modalImage.src = imageData.src;
            modalImage.alt = imageData.alt;
        }
    }
    setupIntersectionObserver() {
        // Animate elements when they come into view
        if ('IntersectionObserver' in window) {
            const animationObserver = new IntersectionObserver((entries)=>{
                entries.forEach((entry)=>{
                    if (entry.isIntersecting) {
                        const element = entry.target;
                        element.style.opacity = '1';
                        element.style.transform = 'translateY(0)';
                        animationObserver.unobserve(element);
                    }
                });
            }, {
                threshold: 0.1,
                rootMargin: '0px 0px -50px 0px'
            });
            // Observe animated elements
            const animatedElements = document.querySelectorAll('.feature-card, .team-member, .before-after-item');
            animatedElements.forEach((element)=>animationObserver.observe(element));
        } else {
            // Fallback for browsers without Intersection Observer
            const animatedElements = document.querySelectorAll('.feature-card, .team-member, .before-after-item');
            animatedElements.forEach((element)=>{
                element.style.opacity = '1';
                element.style.transform = 'translateY(0)';
            });
        }
    }
    // Utility method for throttling scroll events
    throttle(func, delay) {
        let timeoutId;
        let lastExecTime = 0;
        return function(...args) {
            const currentTime = Date.now();
            if (currentTime - lastExecTime > delay) {
                func.apply(this, args);
                lastExecTime = currentTime;
            } else {
                clearTimeout(timeoutId);
                timeoutId = setTimeout(()=>{
                    func.apply(this, args);
                    lastExecTime = Date.now();
                }, delay - (currentTime - lastExecTime));
            }
        };
    }
}
// Initialize when DOM is loaded
document.addEventListener('DOMContentLoaded', ()=>{
    new DrZycWebsite();
});
// Handle window resize
window.addEventListener('resize', ()=>{
    const website = window.drZycWebsite;
    if (website && website.isMenuOpen && window.innerWidth > 768) website.closeMobileMenu();
});

//# sourceMappingURL=drzyc.co.il.8fc866c6.js.map
